import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";
import FinancialAnalytics from "./sections/FinancialAnalytics";
import AIReviewSection from "./sections/AIReviewSection";
import PartnerReviewSection, { collectEscalatedItems } from "./sections/PartnerReviewSection";
import GLSearchPanel from "./checklist/tools/GLSearchPanel";
import AJEScratchPad from "./checklist/tools/AJEScratchPad";
import TrialBalanceQuickView from "./checklist/tools/TrialBalanceQuickView";
import AddRequestModal from "./checklist/prompts/AddRequestModal";
import ClientRequestsSection from "./sections/ClientRequestsSection";
import ExecutionSteps from "./checklist/ExecutionSteps";
import ToolsReviewSection from "../../../kit/ToolsReviewSection";
import ToolsReviewItem, { ToolsReviewResolution, ToolsReviewTools } from "../../../kit/ToolsReviewItem";
import AnalyticsSummaryService from "../../../../../../services/AnalyticsSummaryService";
import { buildCsrsChecklist, countChecklistStatuses } from "./checklist/csrsChecklist";
import { NOTE_TEMPLATES } from "../../shared/helpers/constants";
import WComponent from "../../../../../common/WComponent";
import "../../afs-walkthrough.css";

class ReviewChecklist extends WComponent {
	state = {
		reviewTab: this.props.initialReviewTab || "checklist",
		checklistFilter: "fail",
		activeToolForCheck: null, // { checkId, tool: "gl_search"|"aje_pad"|"tb_view", context: {}, fromTool: string|null }
		toolCardHeight: 500,
		signingOff: false,
		crossCheckAcknowledged: {},
		expandedSections: {},
		addedNotes: [],
		reportDateDrafts: {},
		expandedChecks: {},
		recommendationAcknowledged: {},
		partnerReviews: (function () {
			try { let saved = localStorage.getItem("partner_review_" + this.props.clientId); return saved ? JSON.parse(saved) : {}; } catch (e) { return {}; }
		}).call(this),
	};

	dragRef = null;

	componentDidMount() {
		let { initialReviewTab } = this.props;
		if (initialReviewTab) this.update({ reviewTab: initialReviewTab });
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevProps.initialReviewTab !== this.props.initialReviewTab) {
			let { initialReviewTab } = this.props;
			if (initialReviewTab) this.update({ reviewTab: initialReviewTab });
		}
	}

	handleDragStart = (e) => {
		e.preventDefault();
		let startY = e.clientY;
		let startHeight = this.state.toolCardHeight;

		let onMove = (moveEvent) => {
			let delta = moveEvent.clientY - startY;
			let newHeight = Math.max(250, Math.min(900, startHeight + delta));
			this.update({ toolCardHeight: newHeight });
		};
		let onUp = () => {
			document.removeEventListener("mousemove", onMove);
			document.removeEventListener("mouseup", onUp);
		};
		document.addEventListener("mousemove", onMove);
		document.addEventListener("mouseup", onUp);
	};

	handlePartnerReviewChange = (escalationKey, reviewData) => {
		let { clientId } = this.props;
		this.setState(function (prev) {
			let updated = { ...prev.partnerReviews, [escalationKey]: reviewData };
			try { localStorage.setItem("partner_review_" + clientId, JSON.stringify(updated)); } catch (e) {}
			return { partnerReviews: updated };
		});
	};

	setRecommendationAcknowledged = (valueOrUpdater) => {
		if (typeof valueOrUpdater === "function") {
			this.setState(function (prev) { return { recommendationAcknowledged: valueOrUpdater(prev.recommendationAcknowledged) }; });
		} else {
			this.update({ recommendationAcknowledged: valueOrUpdater });
		}
	};

	render() {
	let { clientId, balanceSheet, incomeStatement, transactions, adjustingEntries, ppeNote, compilationData, compilationReportSignature, compilationReport, manualChecks, onManualCheckToggle, aiReview, aiReviewLoading, onGenerateAiReview, onNavigateToTab, entityName, yearEnd, province, crossCheckResults, crossCheckFacts, chartOfAccounts, canonicalTaxonomyOptions, onCreateChartOfAccount, agentProposals, onApproveAJE, onEditAJEDraft, onApproveContextChange, onApproveAccountMapping, onApproveReclassification, onAddNote, onSignOff, signedOff, clientRequestData, onAddClientRequest, onSendClientRequests, onUpdateClientRequestItem, onDeleteClientRequestItem, clientEmail, onChangeStatementSetting, importedTrialBalance } = this.props;
	let { reviewTab, checklistFilter, activeToolForCheck, toolCardHeight, signingOff, crossCheckAcknowledged, expandedSections, addedNotes, reportDateDrafts, expandedChecks, recommendationAcknowledged, partnerReviews } = this.state;
	let handleDragStart = this.handleDragStart;
	let handlePartnerReviewChange = this.handlePartnerReviewChange;
	let setRecommendationAcknowledged = this.setRecommendationAcknowledged;
	let escalatedItems = collectEscalatedItems(clientId, crossCheckResults);
	let pendingPartnerReviewCount = escalatedItems.filter(function (item) { return !partnerReviews[item.escalationKey]; }).length;

	// Account list for ExecutionSteps — sourced from the canonical trial balance,
	// which always exists after a compilation has been run.
	let effectiveAccounts = crossCheckFacts && crossCheckFacts.allEntries ? crossCheckFacts.allEntries.map(function (e) {
		return { accountNumber: e.canonical_code, description: e.display_name, displayLabel: e.display_name };
	}) : [];

	let csrsChecklist = buildCsrsChecklist({
		balanceSheet: balanceSheet, incomeStatement: incomeStatement, transactions: transactions,
		adjustingEntries: adjustingEntries, ppeNote: ppeNote, compilationData: compilationData,
		compilationReportSignature: compilationReportSignature,
		compilationReport: compilationReport,
		addedNotes: addedNotes, entityName: entityName, yearEnd: yearEnd, crossCheckResults: crossCheckResults,
		importedTrialBalance: importedTrialBalance,
	});

	let counts = countChecklistStatuses({
		csrsChecklist: csrsChecklist, manualChecks: manualChecks, aiReview: aiReview,
		recommendationAcknowledged: recommendationAcknowledged,
		crossCheckResults: crossCheckResults, crossCheckAcknowledged: crossCheckAcknowledged,
	});
	let { manualCheckItems, passCount, failCount, warnCount, infoCount, naCount, blockedCount, manualCompleteCount, totalRecommendations, acknowledgedCount, totalChecks, completedChecks, isReadyForExport } = counts;

	let analyticsSummary = AnalyticsSummaryService.computeAnalyticsSummary({ balanceSheet: balanceSheet, incomeStatement: incomeStatement });

	// ─── Render ──────────────────────────────────────────────────────────

	return (
		<div className="afs-review">
			{/* Summary bar — changes based on active tab */}
			{reviewTab === "checklist" && (
				<div className="afs-review__summary">
					<div className={clsx("afs-review__summary-item", "afs-review__summary-item--clickable", checklistFilter === "fail" && "afs-review__summary-item--active")} onClick={() => { this.update({ checklistFilter: checklistFilter === "fail" ? null : "fail" }); }}>
						<div className="afs-review__summary-value afs-review__summary-value--fail">{failCount}</div>
						<div className="afs-review__summary-label">Failed</div>
					</div>
					<div className={clsx("afs-review__summary-item", "afs-review__summary-item--clickable", checklistFilter === "warn" && "afs-review__summary-item--active")} onClick={() => { this.update({ checklistFilter: checklistFilter === "warn" ? null : "warn" }); }}>
						<div className="afs-review__summary-value afs-review__summary-value--warn">{warnCount}</div>
						<div className="afs-review__summary-label">Warnings</div>
					</div>
					{blockedCount > 0 && (
						<div className={clsx("afs-review__summary-item", "afs-review__summary-item--clickable", checklistFilter === "blocked" && "afs-review__summary-item--active")} onClick={() => { this.update({ checklistFilter: checklistFilter === "blocked" ? null : "blocked" }); }}>
							<div className="afs-review__summary-value afs-review__summary-value--blocked">{blockedCount}</div>
							<div className="afs-review__summary-label">Blocked</div>
						</div>
					)}
					{infoCount > 0 && (
						<div className={clsx("afs-review__summary-item", "afs-review__summary-item--clickable", checklistFilter === "info" && "afs-review__summary-item--active")} onClick={() => { this.update({ checklistFilter: checklistFilter === "info" ? null : "info" }); }}>
							<div className="afs-review__summary-value afs-review__summary-value--info">{infoCount}</div>
							<div className="afs-review__summary-label">Advisory</div>
						</div>
					)}
					<div className={clsx("afs-review__summary-item", "afs-review__summary-item--clickable", checklistFilter === "pending" && "afs-review__summary-item--active")} onClick={() => { this.update({ checklistFilter: checklistFilter === "pending" ? null : "pending" }); }}>
						<div className="afs-review__summary-value afs-review__summary-value--pending">{manualCheckItems.length - manualCompleteCount}</div>
						<div className="afs-review__summary-label">To Sign Off</div>
					</div>
					{naCount > 0 && (
						<div className={clsx("afs-review__summary-item", "afs-review__summary-item--clickable", checklistFilter === "na" && "afs-review__summary-item--active")} onClick={() => { this.update({ checklistFilter: checklistFilter === "na" ? null : "na" }); }}>
							<div className="afs-review__summary-value afs-review__summary-value--na">{naCount}</div>
							<div className="afs-review__summary-label">N/A</div>
						</div>
					)}
					<div className={clsx("afs-review__summary-item", "afs-review__summary-item--clickable", checklistFilter === "pass" && "afs-review__summary-item--active")} onClick={() => { this.update({ checklistFilter: checklistFilter === "pass" ? null : "pass" }); }}>
						<div className="afs-review__summary-value afs-review__summary-value--pass">{passCount}</div>
						<div className="afs-review__summary-label">Passed</div>
					</div>
					<div className={clsx("afs-review__summary-item", "afs-review__summary-item--clickable", checklistFilter === null && "afs-review__summary-item--active")} onClick={() => { this.update({ checklistFilter: null }); }}>
						<div className="afs-review__summary-value">{completedChecks}/{totalChecks}</div>
						<div className="afs-review__summary-label">Complete</div>
					</div>
				</div>
			)}

			{reviewTab === "analytics" && (
				<div className="afs-review__summary">
					<div className="afs-review__summary-item">
						<div className="afs-review__summary-value">{analyticsSummary.currentRatio}</div>
						<div className="afs-review__summary-label">Current Ratio</div>
					</div>
					<div className="afs-review__summary-item">
						<div className="afs-review__summary-value">{analyticsSummary.grossMargin}</div>
						<div className="afs-review__summary-label">Gross Margin</div>
					</div>
					<div className="afs-review__summary-item">
						<div className="afs-review__summary-value">{analyticsSummary.netMargin}</div>
						<div className="afs-review__summary-label">Net Margin</div>
					</div>
					<div className="afs-review__summary-item">
						<div className={clsx("afs-review__summary-value", analyticsSummary.netIncomeRaw >= 0 ? "afs-review__summary-value--pass" : "afs-review__summary-value--fail")}>{analyticsSummary.netIncome}</div>
						<div className="afs-review__summary-label">Net Income</div>
					</div>
				</div>
			)}

			{reviewTab === "ai" && (
				<div className="afs-review__summary">
					<div className="afs-review__summary-item">
						<div className="afs-review__summary-value">{aiReview ? (aiReview.financialIndicators || []).length : "–"}</div>
						<div className="afs-review__summary-label">Indicators</div>
					</div>
					<div className="afs-review__summary-item">
						<div className="afs-review__summary-value afs-review__summary-value--warn">{aiReview ? (aiReview.anomaliesAndConcerns || []).length : "–"}</div>
						<div className="afs-review__summary-label">Concerns</div>
					</div>
					<div className="afs-review__summary-item">
						<div className="afs-review__summary-value">{totalRecommendations || "–"}</div>
						<div className="afs-review__summary-label">Recommendations</div>
					</div>
					<div className="afs-review__summary-item">
						<div className={clsx("afs-review__summary-value", acknowledgedCount === totalRecommendations && totalRecommendations > 0 ? "afs-review__summary-value--pass" : "")}>{totalRecommendations > 0 ? acknowledgedCount + "/" + totalRecommendations : "–"}</div>
						<div className="afs-review__summary-label">Addressed</div>
					</div>
				</div>
			)}

			{/* Sub-tabs */}
			<div className="afs-review__ai-tabs">
				<button className={clsx("afs-review__ai-tab", reviewTab === "checklist" && "afs-review__ai-tab--active")} onClick={() => { this.update({ reviewTab: "checklist" }); }}>Checklist</button>
				<button className={clsx("afs-review__ai-tab", reviewTab === "analytics" && "afs-review__ai-tab--active")} onClick={() => { this.update({ reviewTab: "analytics" }); }}>Analytics</button>
				<button className={clsx("afs-review__ai-tab", reviewTab === "ai" && "afs-review__ai-tab--active")} onClick={() => { this.update({ reviewTab: "ai" }); }}>AI Review</button>
				<button className={clsx("afs-review__ai-tab", reviewTab === "partner" && "afs-review__ai-tab--active")} onClick={() => { this.update({ reviewTab: "partner" }); }}>
					Partner Review
					{pendingPartnerReviewCount > 0 && <span className="afs-review__partner-badge">{pendingPartnerReviewCount}</span>}
				</button>
				<button className={clsx("afs-review__ai-tab", reviewTab === "requests" && "afs-review__ai-tab--active")} onClick={() => { this.update({ reviewTab: "requests" }); }}>
					Client Requests
					{clientRequestData && clientRequestData.items && clientRequestData.items.length > 0 && (
						<span className="afs-review__partner-badge">{clientRequestData.items.filter(function (i) { return i.blocking && i.responseStatus === "pending"; }).length || clientRequestData.items.length}</span>
					)}
				</button>
			</div>

			{/* Tab: Checklist */}
			{reviewTab === "checklist" && (
				<div>
					{csrsChecklist.map((group) => {
						let groupItems = group.items;
						if (checklistFilter) {
							groupItems = groupItems.filter(function (item) {
								if (checklistFilter === "pending") return !item.auto && !manualChecks[item.id];
								let isNA = item.auto && item.detail && item.detail.startsWith("N/A");
								if (checklistFilter === "na") return isNA;
								if (checklistFilter === "pass" && isNA) return false;  // N/A items hidden when filtering "pass"
								if (item.auto) return item.status === checklistFilter;
								if (!item.auto) {
									if (checklistFilter === "pass") return manualChecks[item.id];
									return false;
								}
								return true;
							});
						}
						if (groupItems.length === 0) return null;

						// Sort by severity: failed first, then blocked, warn, info, pass, N/A
						let severityOrder = { fail: 0, blocked: 1, warn: 2, info: 3, pass: 4 };
						groupItems = groupItems.slice().sort(function (a, b) {
							let isNA_a = a.auto && a.detail && a.detail.startsWith("N/A");
							let isNA_b = b.auto && b.detail && b.detail.startsWith("N/A");
							let sa = isNA_a ? 5 : (severityOrder[a.status] != null ? severityOrder[a.status] : 4);
							let sb = isNA_b ? 5 : (severityOrder[b.status] != null ? severityOrder[b.status] : 4);
							return sa - sb;
						});

						let groupCompleted = 0;
						for (let item of group.items) {
							if (item.auto && item.status === "pass") groupCompleted++;
							else if (!item.auto && manualChecks[item.id]) groupCompleted++;
						}

						let allPassed = groupCompleted === group.items.length;
						let isExpanded = expandedSections[group.section] != null ? expandedSections[group.section] : !allPassed;

						return (
							<ToolsReviewSection
								key={group.section}
								title={group.section}
								meta={group.ref}
								doneCount={groupCompleted}
								totalCount={group.items.length}
								isExpanded={isExpanded}
								onToggle={() => { this.setState(function (prev) { return { expandedSections: { ...prev.expandedSections, [group.section]: !isExpanded } }; }); }}
							>
								{groupItems.map((item) => {
									if (item.auto) {
										let isNA = item.detail && item.detail.startsWith("N/A");
										let isBlocked = item.status === "blocked";
										let isPassed = item.status === "pass" && !isNA;

										// Passed cross-checks — expandable to see what was actually evaluated
										if (isPassed && item.crossCheck) {
											let isCheckExpanded = expandedChecks[item.checkId] || false;
											let matchingCheck = item.checkId ? (crossCheckResults || []).find(function (check) { return check.id === item.checkId; }) : null;
											let supporting = matchingCheck && matchingCheck.supporting ? matchingCheck.supporting : null;
											return (
												<div key={item.id} className={clsx("afs-review__item", "afs-review__item--pass", "afs-review__item--clickable")} onClick={() => { this.setState(function (prev) { return { expandedChecks: { ...prev.expandedChecks, [item.checkId]: !isCheckExpanded } }; }); }}>
													<Icon.CheckCircle size={18} color="#2e7d32" className="afs-review__icon" />
													<div className="afs-review__content">
														<p className="afs-review__label">{item.label}</p>
														<p className="afs-review__detail afs-review__detail--pass">{item.detail}</p>
														{isCheckExpanded && (
															<div className="afs-review__check-evidence" onClick={function (e) { e.stopPropagation(); }}>
																{supporting && supporting.observed && supporting.observed.length > 0 ? (
																	<div className="afs-review__check-evidence-details">
																		<div className="afs-review__check-evidence-section">
																			<span className="afs-review__check-evidence-label">Checked:</span>
																			{supporting.observed.map(function (obs, i) {
																				return <span key={i} className="afs-review__check-evidence-item">{obs.display_name || obs.description || obs.canonical_code}{obs.sum != null ? " ($" + Math.abs(obs.sum).toLocaleString("en-CA") + ")" : ""}</span>;
																			})}
																		</div>
																		{supporting.materiality_threshold && (
																			<span className="afs-review__check-evidence-item">Materiality: ${supporting.materiality_threshold.toLocaleString("en-CA")}</span>
																		)}
																	</div>
																) : matchingCheck && matchingCheck.passed_reason ? null : (
																	<div className="afs-review__check-evidence-details">
																		<span className="afs-review__check-evidence-item">No material balances found — check passed by default</span>
																	</div>
																)}
															</div>
														)}
													</div>
												</div>
											);
										}

										let reviewStatus = isNA ? "na" : isBlocked ? "blocked" : item.status;

										// Look up resolution for failing cross-check items
										let resolution = null;
										let executionStepStatuses = null;
										if (item.crossCheck && !item.detail?.startsWith("N/A") && item.status !== "pass" && item.checkId) {
											let matchingCheck = (crossCheckResults || []).find(function (check) { return check.id === item.checkId; });
											if (matchingCheck && matchingCheck.resolution) resolution = matchingCheck.resolution;
											if (matchingCheck && matchingCheck.execution_step_statuses) executionStepStatuses = matchingCheck.execution_step_statuses;
										}

										return (
											<ToolsReviewItem
												key={item.id}
												id={"check-" + item.id}
												status={reviewStatus}
												detailTone={item.status}
												label={item.label}
												detail={item.detail}
												after={
													<>
														{activeToolForCheck && activeToolForCheck.itemId === item.id && activeToolForCheck.tool !== "add_request" && (
															<div className="afs-review__inline-tool-card" style={{ maxHeight: toolCardHeight + "px", height: toolCardHeight + "px" }}>
																{activeToolForCheck.tool === "gl_search" && <GLSearchPanel transactions={transactions} chartOfAccounts={chartOfAccounts} initialSearch={(activeToolForCheck.context && activeToolForCheck.context.initialSearch) || ""} onReclassify={onApproveReclassification} onClose={() => { this.update({ activeToolForCheck: null }); }} onBack={activeToolForCheck.fromTool === "tb_view" ? () => { this.update({ activeToolForCheck: { itemId: item.id, checkId: item.checkId, tool: "tb_view", context: {} } }); } : null} />}
																{activeToolForCheck.tool === "aje_pad" && <AJEScratchPad chartOfAccounts={chartOfAccounts} canonicalTaxonomyOptions={canonicalTaxonomyOptions} onCreateChartOfAccount={onCreateChartOfAccount} yearEnd={yearEnd} onApproveAJE={onApproveAJE} onClose={() => { this.update({ activeToolForCheck: null }); }} />}
																{activeToolForCheck.tool === "tb_view" && <TrialBalanceQuickView crossCheckFacts={crossCheckFacts} onClose={() => { this.update({ activeToolForCheck: null }); }} onSearchGL={(name) => { this.update({ activeToolForCheck: { itemId: item.id, checkId: item.checkId, tool: "gl_search", context: { initialSearch: name }, fromTool: "tb_view" } }); }} />}
																{activeToolForCheck.tool === "add_request" && null}
																<div className="afs-review__inline-tool-drag" onMouseDown={handleDragStart} title="Drag to resize">
																	<div className="afs-review__inline-tool-drag-line" />
																</div>
															</div>
														)}
														{activeToolForCheck && activeToolForCheck.itemId === item.id && activeToolForCheck.tool === "add_request" && (
															<div className="afs-review__inline-add-request">
																<AddRequestModal checkId={item.checkId} checkLabel={activeToolForCheck.context && activeToolForCheck.context.label} checkDetail={activeToolForCheck.context && activeToolForCheck.context.detail} checkTier={activeToolForCheck.context && activeToolForCheck.context.tier} onAdd={onAddClientRequest} onClose={() => { this.update({ activeToolForCheck: null }); }} />
															</div>
														)}
													</>
												}
											>
												{item.findings && item.findings.length > 0 && (
													<div className="afs-review__findings">
														{item.findings.map((finding, fi) => {
															let codeParts = (finding.canonical_code || "").split(".");
															let shortAccount = codeParts[codeParts.length - 1] || "";
															let absAmount = Math.abs(parseFloat(finding.amount) || 0);
															return (
																<div key={fi} className="afs-review__finding-card">
																	<div className="afs-review__finding-main">
																		<div className="afs-review__finding-desc">{finding.description}</div>
																		<div className="afs-review__finding-actions">
																			<button className="afs-gl-search__trigger" onClick={() => { this.update({ activeToolForCheck: { itemId: item.id, checkId: item.checkId, tool: "gl_search", context: { initialSearch: finding.description } } }); }} title="Search GL">
																				<Icon.Search size={11} /> GL
																			</button>
																			<div className="afs-review__finding-amount">{"$" + absAmount.toLocaleString("en-CA")}</div>
																		</div>
																	</div>
																	<div className="afs-review__finding-meta">
																		<span>{finding.date || "—"}</span>
																		<span className="afs-review__finding-account">{shortAccount.replace(/_/g, " ")}</span>
																	</div>
																</div>
															);
														})}
													</div>
												)}
												{item.actionable && item.actionNoteId && !addedNotes.includes(item.actionNoteId) && (
													addedNotes.includes(item.actionNoteId + "_editing") ? (
														<div className="afs-review__inline-note-editor">
															{(() => {
																let tmpl = NOTE_TEMPLATES[item.actionNoteId];
																let noteFields = tmpl && tmpl.fields ? (typeof tmpl.fields === "function" ? tmpl.fields({ entityName: entityName, province: province, basisOfAccounting: compilationData && compilationData.basisOfAccounting }) : tmpl.fields) : null;
															if (noteFields) {
																	return (
																		<>
																			<div className="afs-review__inline-note-fields">
																				{noteFields.map(function (field) {
																					let fieldId = "note-field-" + item.actionNoteId + "-" + field.id;
																					return (
																						<div key={field.id} className="afs-review__inline-note-field">
																							<label className="afs-review__inline-note-field-label">{field.label}</label>
																							{field.type === "select" ? (
																								<select id={fieldId} className="afs-review__inline-note-field-input" defaultValue={field.default || ""}>
																									{field.options.map(function (opt) { return <option key={opt} value={opt}>{opt}</option>; })}
																								</select>
																							) : (
																								<input id={fieldId} type="text" className="afs-review__inline-note-field-input" defaultValue={field.default || ""} placeholder={field.placeholder || ""} />
																							)}
																						</div>
																					);
																				})}
																			</div>
																			<button className="afs-review__inline-action" onClick={() => {
																				let fieldValues = { entityName: entityName, province: province, basisOfAccounting: compilationData && compilationData.basisOfAccounting };
																				let saveFields = typeof tmpl.fields === "function" ? tmpl.fields({ entityName: entityName, province: province }) : tmpl.fields;
																				for (let field of saveFields) {
																					let el = document.getElementById("note-field-" + item.actionNoteId + "-" + field.id);
																					if (el) fieldValues[field.id] = el.value;
																				}
																				let content = tmpl.template(fieldValues);
																				if (onAddNote) onAddNote(item.actionNoteId, content);
																				this.setState(function (prev) { return { addedNotes: [...prev.addedNotes.filter(function (n) { return n !== item.actionNoteId + "_editing"; }), item.actionNoteId] }; });
																			}}>
																				<Icon.Check size={14} /> Save Note
																			</button>
																		</>
																	);
																}
																// Fallback: textarea for templates without fields
																return (
																	<>
																		<textarea
																			id={"note-editor-" + item.actionNoteId}
																			className="afs-review__inline-note-textarea"
																			defaultValue={tmpl && tmpl.template ? tmpl.template({ entityName: entityName, basisOfAccounting: compilationData && compilationData.basisOfAccounting }) : ""}
																			rows={5}
																		/>
																		<button className="afs-review__inline-action" onClick={() => {
																			let el = document.getElementById("note-editor-" + item.actionNoteId);
																			let content = el ? el.value : "";
																			if (onAddNote) onAddNote(item.actionNoteId, content);
																			this.setState(function (prev) { return { addedNotes: [...prev.addedNotes.filter(function (n) { return n !== item.actionNoteId + "_editing"; }), item.actionNoteId] }; });
																		}}>
																			<Icon.Check size={14} /> Save Note
																		</button>
																	</>
																);
															})()}
														</div>
													) : (
														<button className="afs-review__inline-action" onClick={() => {
															this.setState(function (prev) { return { addedNotes: [...prev.addedNotes, item.actionNoteId + "_editing"] }; });
														}}>
															<Icon.Plus size={14} /> {item.actionLabel}
														</button>
													)
												)}
												{item.setsReportDate && onChangeStatementSetting && (
													<div className="afs-review__report-date" data-test="set-report-date">
														<input
															type="date"
															className="tk-input afs-review__report-date-input"
															value={reportDateDrafts[item.id] || item.suggestedReportDate}
															onChange={event => this.update({ reportDateDrafts: { ...reportDateDrafts, [item.id]: event.target.value } })}
														/>
														<button className="afs-review__inline-action" onClick={() => onChangeStatementSetting({ setting: "compilationReportDate", value: reportDateDrafts[item.id] || item.suggestedReportDate })}>
															<Icon.Calendar size={14} /> Set report date
														</button>
													</div>
												)}
												{resolution && (
													<ToolsReviewResolution
														text={resolution.suggestion}
														kindLabel={resolution.type === "aje" ? "Adjusting Entry" : resolution.type === "mapping_fix" ? "Mapping Fix" : resolution.type === "context_fix" ? "Context Change" : resolution.type === "acknowledgement" ? "Acknowledgement" : resolution.type}
														actions={
															<button className="afs-gl-search__trigger" onClick={() => { this.update({ activeToolForCheck: { itemId: item.id, checkId: item.checkId, tool: "gl_search", context: { initialSearch: item.label.split("—")[0].trim() } } }); }} title="Search GL for related transactions">
																<Icon.Search size={11} /> Search GL
															</button>
														}
													>
														{executionStepStatuses ? (
															<>
																{(resolution.steps || []).filter(step => step.recommended).map((step, stepIndex) => (
																	<div key={"recommended-" + stepIndex} className="afs-review__checklist-step afs-review__checklist-step--recommended">
																		<span className="afs-review__checklist-text">{step.text}</span>
																	</div>
																))}
																<ExecutionSteps clientId={clientId} checkId={item.checkId} steps={executionStepStatuses} agentProposals={agentProposals || []} onApproveAJE={onApproveAJE} onEditAJEDraft={onEditAJEDraft} onApproveContextChange={onApproveContextChange} onApproveAccountMapping={onApproveAccountMapping} chartOfAccounts={effectiveAccounts} crossCheckFacts={crossCheckFacts} />
															</>
														) : resolution.steps && resolution.steps.length > 0 ? (
															<div className="afs-review__resolution-steps">
																{resolution.steps.map((step, stepIndex) => {
																	let stepKey = item.checkId + "_step_" + stepIndex;
																	let isStepChecked = crossCheckAcknowledged[stepKey] || false;
																	return (
																		<div key={stepIndex} className={clsx("afs-review__checklist-step", step.recommended && "afs-review__checklist-step--recommended", isStepChecked && "afs-review__checklist-step--checked")} onClick={() => { this.setState(function (prev) { return { crossCheckAcknowledged: { ...prev.crossCheckAcknowledged, [stepKey]: !prev.crossCheckAcknowledged[stepKey] } }; }); }}>
																			{isStepChecked
																				? <Icon.CheckSquare size={16} color="#2e7d32" className="afs-review__checklist-icon" />
																				: <Icon.Square size={16} color="#ccc" className="afs-review__checklist-icon" />
																			}
																			<span className={clsx("afs-review__checklist-text", isStepChecked && "afs-review__checklist-text--done")}>{step.text}</span>
																		</div>
																	);
																})}
															</div>
														) : null}
													</ToolsReviewResolution>
												)}
												{item.status !== "pass" && !isNA && (
													<ToolsReviewTools
														tools={[
															{
																key: "gl_search",
																title: "Search GL",
																icon: <Icon.Search size={12} />,
																onClick: () => { this.update({ activeToolForCheck: activeToolForCheck && activeToolForCheck.itemId === item.id && activeToolForCheck.tool === "gl_search" ? null : { itemId: item.id, checkId: item.checkId, tool: "gl_search", context: { initialSearch: item.label.split("\u2014")[0].trim() } } }); },
															},
															{
																key: "aje_pad",
																title: "Add AJE",
																icon: <Icon.Edit3 size={12} />,
																onClick: () => { this.update({ activeToolForCheck: activeToolForCheck && activeToolForCheck.itemId === item.id && activeToolForCheck.tool === "aje_pad" ? null : { itemId: item.id, checkId: item.checkId, tool: "aje_pad", context: {} } }); },
															},
															{
																key: "tb_view",
																title: "Trial Balance",
																icon: <Icon.List size={12} />,
																onClick: () => { this.update({ activeToolForCheck: activeToolForCheck && activeToolForCheck.itemId === item.id && activeToolForCheck.tool === "tb_view" ? null : { itemId: item.id, checkId: item.checkId, tool: "tb_view", context: {} } }); },
															},
															{
																key: "add_request",
																title: "Add to Client Requests",
																icon: <Icon.Clipboard size={12} />,
																onClick: () => { this.update({ activeToolForCheck: activeToolForCheck && activeToolForCheck.itemId === item.id && activeToolForCheck.tool === "add_request" ? null : { itemId: item.id, checkId: item.checkId, tool: "add_request", context: { label: item.label, detail: item.detail, tier: item.tier } } }); },
															},
														]}
													/>
												)}
											</ToolsReviewItem>
										);
									}

									let isChecked = manualChecks[item.id] || false;
									return (
										<div
											key={item.id}
											className={clsx("afs-review__item", isChecked ? "afs-review__item--manual-checked" : "afs-review__item--manual")}
											onClick={function () { onManualCheckToggle(item.id); }}
										>
											{isChecked
												? <Icon.CheckSquare size={18} color="#2e7d32" className="afs-review__icon" />
												: <Icon.Square size={18} color="#ccc" className="afs-review__icon" />
											}
											<div className="afs-review__content">
												<p className="afs-review__label">{item.label}</p>
												<p className="afs-review__detail">{item.detail}</p>
											</div>
										</div>
									);
								})}
							</ToolsReviewSection>
						);
					})}

					{(!checklistFilter || checklistFilter === "fail") && (
						<div className={clsx("afs-review__export-gate", isReadyForExport ? "afs-review__export-gate--ready" : "afs-review__export-gate--blocked")}>
							{signedOff
								? "Engagement signed off and finalized."
								: isReadyForExport
								? "All checks passed. Ready to sign off."
								: "Resolve all failed checks and complete sign-offs before exporting."
							}
							{isReadyForExport && !signedOff && onSignOff && (
								<button
									className="afs-review__signoff-btn"
									disabled={signingOff}
									onClick={async () => {
										this.update({ signingOff: true });
										await onSignOff();
										this.update({ signingOff: false });
									}}
								>
									<Icon.Shield size={14} /> {signingOff ? "Signing Off..." : "Sign Off & Finalize"}
								</button>
							)}
							{signedOff && (
								<span className="afs-review__signed-badge">
									<Icon.CheckCircle size={14} /> Signed
								</span>
							)}
						</div>
					)}
				</div>
			)}

			{/* Tab: Analytics */}
			{reviewTab === "analytics" && (
				<FinancialAnalytics balanceSheet={balanceSheet} incomeStatement={incomeStatement} province={province} />
			)}

			{/* Tab: AI Review */}
			{reviewTab === "ai" && (
				<AIReviewSection aiReview={aiReview} aiReviewLoading={aiReviewLoading} onGenerate={onGenerateAiReview} recommendationAcknowledged={recommendationAcknowledged} onRecommendationAcknowledgedChange={setRecommendationAcknowledged} onNavigateToTab={onNavigateToTab} />
			)}

			{/* Tab: Partner Review */}
			{reviewTab === "partner" && (
				<PartnerReviewSection clientId={clientId} crossCheckResults={crossCheckResults} partnerReviews={partnerReviews} onPartnerReviewChange={handlePartnerReviewChange} />
			)}

			{/* Tab: Client Requests */}
			{reviewTab === "requests" && (
				<ClientRequestsSection
					requestData={clientRequestData}
					clientEmail={clientEmail}
					onSend={onSendClientRequests}
					onUpdateItem={onUpdateClientRequestItem}
					onDeleteItem={onDeleteClientRequestItem}
					clientName={entityName}
					yearEnd={yearEnd}
				/>
			)}
		</div>
	);
	}
}

export default ReviewChecklist;
